import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { KongCatalogPayload, ServicesStatusPayload } from './types';
import {
  filterExposeRoutes,
  presentRouteLastProbed,
  resolveComponentKey,
  statusToChipColor,
} from './helpers';

type Props = {
  catalog: KongCatalogPayload | undefined;
  status: ServicesStatusPayload | undefined;
  isLoading: boolean;
  error: Error | null;
};

export default function RestRoutesTable({ catalog, status, isLoading, error }: Props) {
  const components = status?.components || {};

  const rows = useMemo(() => {
    if (!catalog?.routes) return [];
    return filterExposeRoutes(catalog.routes).flatMap((route) =>
      (route.paths || []).filter((p) => /public|mcp\/app/i.test(p)).map((path) => {
        const { key, component } = resolveComponentKey(route.service?.name, components);
        return {
          id: `${route.id}-${path}`,
          path,
          routeName: route.name,
          serviceName: route.service?.name || '—',
          componentKey: key,
          health: component?.status || 'unknown',
          lastProbed: presentRouteLastProbed(component, key),
        };
      })
    );
  }, [catalog, components]);

  if (isLoading && !catalog) {
    return (
      <Box sx={{ mb: 3 }}>
        <Skeleton variant="text" width={200} height={28} />
        <Skeleton variant="rounded" height={180} sx={{ mt: 1 }} />
      </Box>
    );
  }

  if (error && !catalog) {
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>
        REST route inventory unavailable: {error.message}
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        Exposed REST routes ({rows.length})
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        unknown means no matching /services/status component — not counted as ok.
      </Typography>
      <TableContainer sx={{ maxHeight: 320, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell><strong>Path</strong></TableCell>
              <TableCell><strong>Upstream</strong></TableCell>
              <TableCell><strong>Health</strong></TableCell>
              <TableCell><strong>Last probed</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No /public/* or /mcp/app/* routes found in Kong catalog.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {row.path}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{row.serviceName}</Typography>
                    {row.componentKey && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        → {row.componentKey}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.health}
                      color={statusToChipColor(row.health as 'ok' | 'degraded' | 'down' | 'unknown')}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {row.lastProbed}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
